import { useAtomValue } from 'jotai'
import React from 'react'

import { type SoundClipOption, soundClipsAtom } from '../../atoms'
import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'
import { NodeSelect } from './NodeSelect'

export type SoundClip = SoundClipOption

export interface SoundClipSelectorProps {
  /** Current selected sound clip ID */
  value?: string
  /** Callback when sound clip selection changes */
  onChange: (
    soundClipId: string | undefined,
    soundClipUuid: string | undefined,
  ) => void
  /** Placeholder text for the select */
  placeholder?: string
  /** Label for the floating label */
  label?: string
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
  // Note: soundClips is provided via Jotai atoms (soundClipsAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

/**
 * Reusable sound clip selector dropdown component.
 *
 * Data Source: Sound clips from soundClipsAtom (Jotai atom)
 * Fetched via: GET /app/v1/sound-clips/?pbx_id={id}
 * Documentation: libraries/switchboard-ui/NODE_DATA_POPULATION/sound-clip.md
 * Components: SoundClipNode and AutoAttendantNode use SoundClipSelector
 */
export const SoundClipSelector: React.FC<SoundClipSelectorProps> = ({
  value = '',
  onChange,
  placeholder: placeholderProp,
  label: labelProp,
  t,
}) => {
  const placeholder =
    placeholderProp ??
    text(t, 'switchboard.core.selectSoundClip', 'Select sound clip...')
  const label =
    labelProp ?? text(t, 'switchboard.core.soundClipLabel', 'Sound Clip')
  const soundClips = useAtomValue(soundClipsAtom)

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const soundClipId = e.target.value || undefined
      const soundClipUuid = soundClips.find(
        (clip) => clip.id === soundClipId,
      )?.uuid
      onChange(soundClipId, soundClipUuid)
    },
    [onChange, soundClips],
  )

  return (
    <NodeSelect label={label} value={value} onChange={handleChange}>
      <option value="">{placeholder}</option>
      {soundClips.map((clip) => (
        <option key={clip.id} value={clip.id}>
          {clip.name || clip.id}
        </option>
      ))}
    </NodeSelect>
  )
}
